"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { FiX } from "react-icons/fi";
import { ServiceSearchSelect } from "@/components/service-search-select";
import Button from "@/components/ui/button";
import { DurationInput } from "@/components/ui/duration-input";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocationTimezone } from "@/hooks/use-timezone";
import { REBOOKING_ADJUST_MINUTES_STEP, REBOOKING_DURATION_UNITS, REBOOKING_MAX_ADJUST_MINUTES, adjustTimeLabel, durationToInterval, intervalToMinutes, readAdjustMinutes, readRebookingDefaults, rebookingDueDay, toDurationUnit } from "@/lib/rebooking-defaults";
import { Rebooking, RebookingIntervalUnit, RebookingSelection } from "@/types/forms/question";
import type { NoteAnswerDetail } from "@/types/notes";
import type { ServiceTable } from "@/types/service";
import { serviceQueryOptions } from "@/api/provider/admin/service-query";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
interface RebookingAnswerEditorProps {
  answer: NoteAnswerDetail;
  onChange: (answerId: string, data: Record<string, unknown>) => void;
  disabled?: boolean;
  title?: string;
  questionData?: Rebooking;
  /** The note this answer belongs to, read for its appointment's start. */
  noteId?: string;
}

/** "in 6 weeks" — the phrase the front desk reads on the checklist step. */
const INTERVAL_PHRASES: Record<RebookingIntervalUnit, (value: number) => string> = {
  [RebookingIntervalUnit.DAY]: v => `in ${v} ${v === 1 ? "day" : "days"}`,
  [RebookingIntervalUnit.WEEK]: v => `in ${v} ${v === 1 ? "week" : "weeks"}`,
  [RebookingIntervalUnit.MONTH]: v => `in ${v} ${v === 1 ? "month" : "months"}`
};
function readSelections(data: NoteAnswerDetail["data"]): RebookingSelection[] {
  const raw = (data as {
    rebookings?: unknown;
  })?.rebookings;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap(entry => {
    if (typeof entry !== "object" || entry === null) return [];
    const {
      serviceId,
      intervalValue,
      intervalUnit,
      adjustMinutes
    } = entry as Partial<RebookingSelection>;
    if (typeof serviceId !== "string" || !serviceId) return [];
    const unit = Object.values(RebookingIntervalUnit).includes(intervalUnit as RebookingIntervalUnit) ? intervalUnit as RebookingIntervalUnit : RebookingIntervalUnit.WEEK;
    const selection: RebookingSelection = {
      serviceId,
      intervalValue: typeof intervalValue === "number" && intervalValue > 0 ? intervalValue : 1,
      intervalUnit: unit
    };
    // Older answers have none; leave the key off rather than write null.
    const adjust = readAdjustMinutes(adjustMinutes);
    if (adjust) selection.adjustMinutes = adjust;
    return [selection];
  });
}

/**
 * "Around Dec 22, 2026", counted on the note's location clock, the same one
 * the front desk's Rebook step counts on. Its own component because the
 * patient portal renders this editor without the provider's location data;
 * it only mounts for a provider note with an appointment.
 */
function RebookingDueDayCaption(t0) {
  const $ = _c(7);
  const {
    appointmentStart,
    locationId,
    selection
  } = t0;
  const timeZone = useLocationTimezone(locationId);
  let t1;
  if ($[0] !== appointmentStart || $[1] !== selection.intervalUnit || $[2] !== selection.intervalValue || $[3] !== timeZone) {
    t1 = rebookingDueDay(appointmentStart, selection.intervalValue, selection.intervalUnit, timeZone);
    $[0] = appointmentStart;
    $[1] = selection.intervalUnit;
    $[2] = selection.intervalValue;
    $[3] = timeZone;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = `Around ${t1}`;
  let t3;
  if ($[5] !== t2) {
    t3 = <p className="truncate text-caption text-foreground-muted">{t2}</p>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export const RebookingAnswerEditor: React.FC<RebookingAnswerEditorProps> = t0 => {
  const $ = _c(20);
  const {
    answer,
    onChange,
    disabled: t1,
    title,
    questionData,
    noteId
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const selections = readSelections(answer.data);
  let t2;
  if ($[0] !== noteId) {
    t2 = noteQueryOptions.detail(noteId ?? "");
    $[0] = noteId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !!noteId;
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: note
  } = useQuery(t4);
  const appointmentStart = note?.eventId ? note.visitDate : undefined;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = serviceQueryOptions.list();
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    data: services,
    isLoading
  } = useQuery(t5);
  let t6;
  if ($[6] !== services) {
    t6 = new Map((services ?? []).map(_temp));
    $[6] = services;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const namesById = t6;
  let t7;
  if ($[8] !== answer.data || $[9] !== answer.id || $[10] !== onChange) {
    t7 = next => {
      onChange(answer.id, {
        ...answer.data,
        rebookings: next
      });
    };
    $[8] = answer.data;
    $[9] = answer.id;
    $[10] = onChange;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const updateSelections = t7;
  const handleAdd = serviceId => {
    if (!serviceId) {
      return;
    }
    if (selections.some(selection => selection.serviceId === serviceId)) {
      return;
    }
    const defaults = readRebookingDefaults(questionData);
    updateSelections([...selections, {
      serviceId,
      intervalValue: defaults.intervalValue,
      intervalUnit: defaults.intervalUnit
    }]);
  };
  const handleRemove = serviceId_0 => {
    updateSelections(selections.filter(selection_0 => selection_0.serviceId !== serviceId_0));
  };
  const handleAdjustMinutesChange = (serviceId_1, minutes) => {
    updateSelections(selections.map(selection_1 => {
      if (selection_1.serviceId !== serviceId_1) {
        return selection_1;
      }
      const {
        adjustMinutes: _cleared,
        ...rest
      } = selection_1;
      const adjustMinutes = readAdjustMinutes(minutes);
      return adjustMinutes ? {
        ...rest,
        adjustMinutes
      } : rest;
    }));
  };
  const handleIntervalChange = (serviceId_2, changes) => {
    updateSelections(selections.map(selection_2 => selection_2.serviceId === serviceId_2 ? {
      ...selection_2,
      ...changes
    } : selection_2));
  };
  const t8 = "space-y-2";
  let t9;
  if ($[12] !== title) {
    t9 = title && <Label className="text-body-strong">{title}</Label>;
    $[12] = title;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = !disabled && <ServiceSearchSelect services={services ?? []} value="" onValueChange={handleAdd} selectedServiceIds={selections.map(_temp2)} placeholder="Add a service to rebook" disabled={isLoading} triggerClassName="sm:w-96" />;
  const t11 = isLoading && selections.length > 0 && <div className="space-y-2">{selections.map(_temp3)}</div>;
  const t12 = !isLoading && selections.length === 0 && (disabled ? <p className="text-body text-foreground-muted">—</p> : <p className="text-caption text-foreground-muted">Services added here become a Rebooking step on this appointment for the front desk to book.</p>);
  const t13 = !isLoading && selections.length > 0 && <ul className="divide-y divide-border">{selections.map(selection_5 => {
      const name = namesById.get(selection_5.serviceId) ?? selection_5.serviceId;
      return <li key={selection_5.serviceId} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5"><div className="min-w-0 grow basis-40"><p className="truncate text-body-strong text-foreground">{name}</p>{appointmentStart && <RebookingDueDayCaption appointmentStart={appointmentStart} locationId={note?.locationId} selection={selection_5} />}</div>{disabled ? <span className="shrink-0 text-body text-foreground-muted">{[INTERVAL_PHRASES[selection_5.intervalUnit](selection_5.intervalValue), selection_5.adjustMinutes && adjustTimeLabel(selection_5.adjustMinutes)].filter(Boolean).join(" \xB7 ")}</span> : <div className="ml-auto flex flex-wrap items-center justify-end gap-2"><span className="text-body text-foreground-muted">in</span><DurationInput units={REBOOKING_DURATION_UNITS} min={1} aria-label={`Rebook interval for ${name}`} value={intervalToMinutes(selection_5.intervalValue, selection_5.intervalUnit)} unit={toDurationUnit(selection_5.intervalUnit)} onChange={minutes_0 => {
            if (minutes_0 === undefined) {
              return;
            }
            handleIntervalChange(selection_5.serviceId, durationToInterval(minutes_0, toDurationUnit(selection_5.intervalUnit)));
          }} onUnitChange={(unit, minutes_1) => handleIntervalChange(selection_5.serviceId, durationToInterval(minutes_1 ?? 0, unit))} /><span className="flex items-center gap-2"><span className="text-body text-foreground-muted">±</span><Input type="number" className="w-24" min={-REBOOKING_MAX_ADJUST_MINUTES} max={REBOOKING_MAX_ADJUST_MINUTES} step={REBOOKING_ADJUST_MINUTES_STEP} aria-label={`Time to add or take off ${name}, in minutes`} title="Minutes to add to (or, with a minus, take off) the service's usual length" value={selection_5.adjustMinutes ?? 0} onChange={event => {
              const raw = event.target.value;
              if (!/^-?\d+$/.test(raw)) {
                return;
              }
              const minutes_2 = Number(raw);
              if (Math.abs(minutes_2) > REBOOKING_MAX_ADJUST_MINUTES) {
                return;
              }
              handleAdjustMinutesChange(selection_5.serviceId, minutes_2);
            }} /><span className="text-body text-foreground-muted">min</span></span><Button variant="ghost" size="sm" icon={FiX} onClick={() => handleRemove(selection_5.serviceId)} aria-label={`Remove ${name}`} /></div>}</li>;
    })}</ul>;
  let t14;
  if ($[14] !== t10 || $[15] !== t11 || $[16] !== t12 || $[17] !== t13 || $[18] !== t9) {
    t14 = <div className={t8}>{t9}{t10}{t11}{t12}{t13}</div>;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  return t14;
};
function _temp(service) {
  return [service.id, service.name];
}
function _temp2(selection_3) {
  return selection_3.serviceId;
}
function _temp3(selection_4) {
  return <Skeleton key={selection_4.serviceId} className="h-10 w-full rounded-md" />;
}